import * as i0 from '@angular/core';
import { EventEmitter, Directive, HostBinding, Input, Output, forwardRef, HostListener, NgModule } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { defineCustomElements } from '@ispui/checkbox/loader';

class ISPUICheckbox {
    constructor() {
        /** name of control */
        this.name = '';
        /** bold */
        this.bold = false;
        /** block */
        this.block = false;
        /** wrap */
        this.wrap = false;
        /** label position relative to checkbox */
        this.labelPosition = 'right';
        /** returnType */
        this.returnType = 'boolean';
        /** use custom image from css variable for check icon */
        this.useCustomBackground = false;
        /** use minus icon for checked state */
        this.minus = false;
        this.customFocus = new EventEmitter();
        this.customBlur = new EventEmitter();
        this.el = null;
    }
    /** disabled */
    set disabled(value) {
        if (!this.el) {
            return;
        }
        if (!value) {
            this.el.removeAttribute('disabled');
        }
        else {
            this.el.setAttribute('disabled', '');
        }
    }
    /** checked */
    set checked(value) {
        if (!this.el) {
            return;
        }
        this.el.checked = value;
    }
}
ISPUICheckbox.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ISPUICheckbox, deps: [], target: i0.ɵɵFactoryTarget.Directive });
ISPUICheckbox.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.11", type: ISPUICheckbox, inputs: { name: "name", disabled: "disabled", checked: "checked", bold: "bold", block: "block", wrap: "wrap", labelPosition: "labelPosition", returnType: "returnType", useCustomBackground: "useCustomBackground", minus: "minus" }, outputs: { customFocus: "customFocus", customBlur: "customBlur" }, host: { properties: { "attr.name": "this.name", "attr.bold": "this.bold", "attr.block": "this.block", "attr.wrap": "this.wrap", "attr.labelPosition": "this.labelPosition", "attr.returnType": "this.returnType", "attr.useCustomBackground": "this.useCustomBackground", "attr.minus": "this.minus" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ISPUICheckbox, decorators: [{
            type: Directive
        }], propDecorators: { name: [{
                type: HostBinding,
                args: ['attr.name']
            }, {
                type: Input
            }], disabled: [{
                type: Input
            }], checked: [{
                type: Input
            }], bold: [{
                type: HostBinding,
                args: ['attr.bold']
            }, {
                type: Input
            }], block: [{
                type: HostBinding,
                args: ['attr.block']
            }, {
                type: Input
            }], wrap: [{
                type: HostBinding,
                args: ['attr.wrap']
            }, {
                type: Input
            }], labelPosition: [{
                type: HostBinding,
                args: ['attr.labelPosition']
            }, {
                type: Input
            }], returnType: [{
                type: HostBinding,
                args: ['attr.returnType']
            }, {
                type: Input
            }], useCustomBackground: [{
                type: HostBinding,
                args: ['attr.useCustomBackground']
            }, {
                type: Input
            }], minus: [{
                type: HostBinding,
                args: ['attr.minus']
            }, {
                type: Input
            }], customFocus: [{
                type: Output
            }], customBlur: [{
                type: Output
            }] } });
class ISPUICheckboxValueAccessor extends ISPUICheckbox {
    constructor(elRef) {
        super();
        this.elRef = elRef;
        this.el = elRef.nativeElement;
        this.el.addEventListener('focus', (ev) => {
            this.customFocus.emit(ev);
        });
        this.el.addEventListener('blur', (ev) => {
            this.customBlur.emit(ev);
        });
    }
}
ISPUICheckboxValueAccessor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ISPUICheckboxValueAccessor, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
ISPUICheckboxValueAccessor.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.11", type: ISPUICheckboxValueAccessor, selector: "ispui-checkbox:not([ngControl]):not([formControlName]):not([formControl]):not([ngFormControl]):not([ngModel])", usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ISPUICheckboxValueAccessor, decorators: [{
            type: Directive,
            args: [{
                    selector: 'ispui-checkbox:not([ngControl]):not([formControlName]):not([formControl]):not([ngFormControl]):not([ngModel])',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; } });

/* eslint-disable @typescript-eslint/no-use-before-define */
class ISPUICheckboxControlValueAccessor extends ISPUICheckbox {
    constructor(renderer, elementRef) {
        super();
        this.renderer = renderer;
        this.elementRef = elementRef;
        this.onChange = () => {
            return;
        };
        this.onTouched = () => {
            return;
        };
    }
    writeValue(value) {
        this.renderer.setProperty(this.elementRef.nativeElement, 'checked', value);
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        if (isDisabled) {
            this.renderer.setProperty(this.elementRef.nativeElement, 'disabled', isDisabled);
        }
        else {
            this.renderer.removeAttribute(this.elementRef.nativeElement, 'disabled');
        }
    }
}
ISPUICheckboxControlValueAccessor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ISPUICheckboxControlValueAccessor, deps: [{ token: i0.Renderer2 }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
ISPUICheckboxControlValueAccessor.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.11", type: ISPUICheckboxControlValueAccessor, selector: "ispui-checkbox[ngControl],ispui-checkbox[formControlName],ispui-checkbox[formControl],ispui-checkbox[ngFormControl],ispui-checkbox[ngModel]", host: { listeners: { "change": "onChange($event.detail.checked)", "blur": "onTouched()" } }, providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ISPUICheckboxControlValueAccessor),
            multi: true,
        },
    ], usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ISPUICheckboxControlValueAccessor, decorators: [{
            type: Directive,
            args: [{
                    selector: 'ispui-checkbox[ngControl],ispui-checkbox[formControlName],ispui-checkbox[formControl],ispui-checkbox[ngFormControl],ispui-checkbox[ngModel]',
                    providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => ISPUICheckboxControlValueAccessor),
                            multi: true,
                        },
                    ],
                }]
        }], ctorParameters: function () { return [{ type: i0.Renderer2 }, { type: i0.ElementRef }]; }, propDecorators: { onChange: [{
                type: HostListener,
                args: ['change', ['$event.detail.checked']]
            }], onTouched: [{
                type: HostListener,
                args: ['blur']
            }] } });

class ISPUICheckboxModule {
    constructor() {
        defineCustomElements(window);
    }
}
ISPUICheckboxModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ISPUICheckboxModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
ISPUICheckboxModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ISPUICheckboxModule, declarations: [ISPUICheckboxControlValueAccessor, ISPUICheckboxValueAccessor], exports: [ISPUICheckboxControlValueAccessor, ISPUICheckboxValueAccessor] });
ISPUICheckboxModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ISPUICheckboxModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ISPUICheckboxModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [ISPUICheckboxControlValueAccessor, ISPUICheckboxValueAccessor],
                    exports: [ISPUICheckboxControlValueAccessor, ISPUICheckboxValueAccessor],
                }]
        }], ctorParameters: function () { return []; } });

/**
 * Generated bundle index. Do not edit.
 */

export { ISPUICheckboxControlValueAccessor, ISPUICheckboxModule, ISPUICheckboxValueAccessor };
